import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { lazy, Suspense, useState } from 'react';
import { ChevronLeft, ShieldCheck, ShieldOff, Receipt } from 'lucide-react';
import { api } from '@/lib/api';
import { IndicatorPanel } from '@/components/IndicatorPanel';
import { JournalCard } from '@/components/JournalCard';
import { NewsList } from '@/components/NewsList';
import { OrderPreviewDialog } from '@/components/OrderPreviewDialog';
import { SnowflakeRadar } from '@/components/SnowflakeRadar';
import { RMultipleBar } from '@/components/RMultipleBar';
import { CompareTickerPicker } from '@/components/CompareTickerPicker';
import { LoadingSkeleton } from '@/components/states/LoadingSkeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { EmptyState } from '@/components/states/EmptyState';

// ChartShell pulls in lightweight-charts (~280kB) plus the TradingView pro chart
// embed; it's only relevant on this route, so split it out of the main bundle.
const ChartShell = lazy(() =>
  import('@/widgets/ChartShell').then((m) => ({ default: m.ChartShell })),
);
import { fmtMoney, fmtNumber, fmtSignedMoney, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/positions/$ticker')({
  component: PositionDetail,
});

function PositionDetail() {
  const { ticker } = Route.useParams();
  const pos = useQuery({
    queryKey: ['position', ticker],
    queryFn: () => api.position(ticker),
    refetchInterval: 30_000,
  });
  const profile = useQuery({
    queryKey: ['profile', ticker],
    queryFn: () => api.profile(ticker),
  });
  const orders = useQuery({ queryKey: ['active-orders'], queryFn: api.activeOrders });
  const violations = useQuery({ queryKey: ['violations'], queryFn: api.violations });
  const summary = useQuery({ queryKey: ['summary'], queryFn: api.accountSummary });

  const [orderOpen, setOrderOpen] = useState(false);
  const [orderSide, setOrderSide] = useState<'BUY' | 'SELL'>('BUY');
  const [compareTicker, setCompareTicker] = useState<string | undefined>(undefined);

  if (pos.isLoading) {
    return (
      <div className="space-y-4">
        <LoadingSkeleton variant="row" className="h-12 w-1/3" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }
  if (pos.isError) {
    return (
      <ErrorState
        title="Couldn't load position"
        hint={pos.error instanceof Error ? pos.error.message : undefined}
        onRetry={() => pos.refetch()}
      />
    );
  }
  if (!pos.data) {
    return (
      <EmptyState
        title="Ticker not found"
        hint={`No position data for ${ticker}.`}
      />
    );
  }
  const p = pos.data;
  const pnl = p.walletImpact.unrealizedProfitLoss;
  const pnlPct = (pnl / Math.max(0.01, p.walletImpact.totalCost)) * 100;
  const hasStop = (orders.data ?? []).some(
    (o) =>
      o.ticker === ticker &&
      (o.type === 'STOP' || o.type === 'STOP_LIMIT') &&
      o.side === 'SELL',
  );
  const myViolations =
    violations.data?.violations.filter((v) => v.ticker === ticker) ?? [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/positions"
          className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
        >
          <ChevronLeft className="size-3" /> Positions
        </Link>
        <div className="flex items-center gap-2">
          <CompareTickerPicker
            excludeTicker={ticker}
            value={compareTicker}
            onChange={setCompareTicker}
          />
          <button
            type="button"
            onClick={() => {
              setOrderSide('BUY');
              setOrderOpen(true);
            }}
            className="px-3 py-1.5 rounded-md text-xs bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/40 text-indigo-200 inline-flex items-center gap-1.5"
          >
            <Receipt className="size-3.5" /> Order preview
          </button>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-baseline gap-2">
            <h1 className="text-2xl font-semibold text-zinc-50 tracking-tight">
              {p.instrument.name}
            </h1>
            <span className="font-mono text-xs text-zinc-400">
              {p.instrument.ticker}
            </span>
          </div>
          <p className="text-[12px] text-zinc-400 mt-0.5">
            {profile.data?.industry || p.instrument.sector || ''}
            {profile.data?.exchange ? ` · ${profile.data.exchange}` : ''}
          </p>
        </div>
        <div className="flex items-baseline gap-5">
          <div>
            <div className="text-[11px] uppercase text-zinc-400 tracking-wider">
              Price ({p.instrument.currency})
            </div>
            <div className="text-2xl font-semibold num">
              {p.instrument.currency === 'GBP' ? '£' : p.instrument.currency === 'USD' ? '$' : p.instrument.currency === 'EUR' ? '€' : ''}
              {fmtNumber(p.currentPrice)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-zinc-400 tracking-wider">Value (GBP)</div>
            <div className="text-lg num">{fmtMoney(p.walletImpact.currentValue)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-zinc-400 tracking-wider">P&L (GBP)</div>
            <div className={cn('text-lg num', pnl >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
              {fmtSignedMoney(pnl)} <span className="text-xs">({fmtSignedPercent(pnlPct)})</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Suspense fallback={<LoadingSkeleton variant="chart" />}>
          <ChartShell
            ticker={ticker}
            tvSymbol={p.tvSymbol}
            currencySymbol={p.instrument.currency === 'GBP' ? '£' : p.instrument.currency === 'USD' ? '$' : ''}
            avgCost={p.averagePricePaid}
            stopPrice={
              (orders.data ?? []).find(
                (o) => o.ticker === ticker && (o.type === 'STOP' || o.type === 'STOP_LIMIT'),
              )?.stopPrice
            }
            compareTicker={compareTicker}
          />
          </Suspense>
          <RMultipleBar
            entry={p.averagePricePaid}
            current={p.currentPrice}
            stop={
              (orders.data ?? []).find(
                (o) => o.ticker === ticker && (o.type === 'STOP' || o.type === 'STOP_LIMIT'),
              )?.stopPrice
            }
          />
        </div>
        <div className="space-y-3">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
            <h3 className="text-sm font-medium text-zinc-200 mb-2">Position</h3>
            <Row label="Quantity" value={fmtNumber(p.quantity, 0)} />
            <Row label="Available" value={fmtNumber(p.quantityAvailableForTrading, 0)} />
            <Row label="Avg price" value={fmtNumber(p.averagePricePaid)} />
            <Row label="Total cost" value={fmtMoney(p.walletImpact.totalCost)} />
            <Row label="FX impact" value={fmtSignedMoney(p.walletImpact.fxImpact)} />
            <Row
              label="Stop coverage"
              value={
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-xs',
                    hasStop ? 'text-emerald-400' : 'text-rose-400',
                  )}
                >
                  {hasStop ? (
                    <ShieldCheck className="size-3.5" />
                  ) : (
                    <ShieldOff className="size-3.5" />
                  )}
                  {hasStop ? 'Protected' : 'Unprotected'}
                </span>
              }
              valueClass=""
            />
          </div>

          {myViolations.length > 0 && (
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3">
              <h3 className="text-sm font-medium text-rose-200 mb-2">Rule effects</h3>
              <ul className="space-y-1.5">
                {myViolations.map((v, i) => (
                  <li key={i} className="text-xs text-rose-100">
                    <span className="font-medium">{v.ruleId}</span> — {v.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <SnowflakeRadar position={p} totalAccountValue={summary.data?.totalValue ?? 0} />
          <JournalCard ticker={ticker} />
          <IndicatorPanel ticker={ticker} />
          <NewsList ticker={ticker} />
        </div>
      </div>

      <OrderPreviewDialog
        open={orderOpen}
        onOpenChange={setOrderOpen}
        ticker={ticker}
        defaultPrice={p.currentPrice}
        defaultSide={orderSide}
      />
    </motion.div>
  );
}

function Row({
  label,
  value,
  valueClass = 'text-zinc-100 num',
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs py-1.5 border-b border-zinc-900 last:border-b-0">
      <span className="text-zinc-400">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
